// Fuzzy column matching utility
import { generateColumnSynonyms } from "./terminologyData";

interface ColumnSynonyms {
  [key: string]: string[];
}

// Generate synonyms from unified terminology data
export const COLUMN_SYNONYMS: ColumnSynonyms = generateColumnSynonyms();

// Levenshtein distance for fuzzy matching
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

// Normalize column name for comparison
function normalizeColumnName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[_\-\s]+/g, " ")
    .replace(/[^\w\s]/g, "");
}

// Find best match for a CSV column
export function findBestMatch(
  csvColumn: string,
  targetSchema: string[]
): { field: string; confidence: number } | null {
  const normalized = normalizeColumnName(csvColumn);
  let bestMatch: { field: string; confidence: number } | null = null;
  
  for (const schemaField of targetSchema) {
    // Check for exact match
    if (normalizeColumnName(schemaField) === normalized) {
      return { field: schemaField, confidence: 1.0 };
    }
    
    // Check synonyms
    const synonyms = COLUMN_SYNONYMS[schemaField] || [];
    for (const synonym of synonyms) {
      const normalizedSynonym = normalizeColumnName(synonym);
      
      // Exact synonym match
      if (normalizedSynonym === normalized) {
        return { field: schemaField, confidence: 0.95 };
      }
      
      // Fuzzy match
      const distance = levenshteinDistance(normalized, normalizedSynonym);
      const maxLength = Math.max(normalized.length, normalizedSynonym.length);
      const similarity = 1 - distance / maxLength;
      
      if (similarity > 0.7 && (!bestMatch || similarity > bestMatch.confidence)) {
        bestMatch = { field: schemaField, confidence: similarity };
      }
    }
    
    // Also check against the field name itself
    const fieldNormalized = normalizeColumnName(schemaField);
    const distance = levenshteinDistance(normalized, fieldNormalized);
    const maxLength = Math.max(normalized.length, fieldNormalized.length);
    const similarity = 1 - distance / maxLength;
    
    if (similarity > 0.7 && (!bestMatch || similarity > bestMatch.confidence)) {
      bestMatch = { field: schemaField, confidence: similarity };
    }
  }
  
  return bestMatch;
}

// Auto-map CSV columns to schema
export interface ColumnMapping {
  csvColumn: string;
  mappedTo: string | null;
  confidence: number;
  suggestions: Array<{ field: string; confidence: number }>;
}

export function autoMapColumns(
  csvHeaders: string[],
  schemaFields: string[]
): ColumnMapping[] {
  const mapping: ColumnMapping[] = [];
  
  for (const csvColumn of csvHeaders) {
    const bestMatch = findBestMatch(csvColumn, schemaFields);
    
    // Find top 3 suggestions
    const allMatches: Array<{ field: string; confidence: number }> = [];
    
    for (const schemaField of schemaFields) {
      const match = findBestMatch(csvColumn, [schemaField]);
      if (match && match.confidence > 0.5) {
        allMatches.push(match);
      }
    }
    
    allMatches.sort((a, b) => b.confidence - a.confidence);
    const suggestions = allMatches.slice(0, 3);
    
    mapping.push({
      csvColumn,
      mappedTo: bestMatch && bestMatch.confidence > 0.7 ? bestMatch.field : null,
      confidence: bestMatch?.confidence || 0,
      suggestions,
    });
  }
  
  return mapping;
}

// Get schema fields for different import types
export interface SchemaField {
  field: string;
  label: string;
  required: boolean;
}

export function getSchemaFields(importType: string): SchemaField[] {
  switch (importType) {
    case "players":
      return [
        { field: "name", label: "Full Name", required: false },
        { field: "first_name", label: "First Name", required: false },
        { field: "last_name", label: "Last Name", required: false },
        { field: "squad", label: "Squad / Team", required: false },
        { field: "position", label: "Position", required: false },
        { field: "jersey_number", label: "Jersey Number", required: false },
        { field: "age", label: "Age", required: false },
        { field: "date_of_birth", label: "Date of Birth", required: false },
        { field: "height_cm", label: "Height (cm)", required: false },
        { field: "weight_kg", label: "Weight (kg)", required: false },
        { field: "dominant_foot", label: "Dominant Foot", required: false },
        { field: "external_ref", label: "External Ref", required: false },
      ];
    case "sessions":
      return [
        { field: "external_ref", label: "External Ref", required: false },
        { field: "session_date", label: "Session Date", required: true },
        { field: "start_time", label: "Start Time", required: false },
        { field: "end_time", label: "End Time", required: false },
        { field: "session_type", label: "Session Type", required: false },
        { field: "duration_minutes", label: "Duration (min)", required: false },
        { field: "surface", label: "Surface", required: false },
        { field: "notes", label: "Notes", required: false },
      ];
    case "gps":
      return [
        { field: "player_id", label: "Player ID/Name", required: true },
        { field: "session_date", label: "Date", required: true },
        { field: "duration_minutes", label: "Duration (min)", required: false },
        { field: "total_distance_m", label: "Total Distance (m)", required: false },
        { field: "hsd_distance_m", label: "High Speed Distance (m)", required: false },
        { field: "sprint_distance_m", label: "Sprint Distance (m)", required: false },
        { field: "sprint_count", label: "Sprint Count", required: false },
        { field: "max_velocity_ms", label: "Max Speed (m/s)", required: false },
        { field: "accel_count", label: "Acceleration Count", required: false },
        { field: "decel_count", label: "Deceleration Count", required: false },
        { field: "player_load", label: "Player Load", required: false },
        { field: "hmld_distance_m", label: "HMLD (m)", required: false },
        { field: "hib_count", label: "HIB Count", required: false },
        { field: "hib_duration_s", label: "HIB Duration (s)", required: false },
        { field: "metabolic_power_avg", label: "Avg Metabolic Power", required: false },
        { field: "work_ratio", label: "Work Ratio", required: false },
        { field: "rpe", label: "RPE (1-10)", required: false },
        { field: "notes", label: "Notes", required: false },
      ];
    case "srpe":
      return [
        { field: "session_id", label: "Session ID", required: true },
        { field: "player_id", label: "Player ID", required: true },
        { field: "rpe", label: "RPE (1-10)", required: true },
        { field: "duration_minutes", label: "Duration (min)", required: false },
      ];
    case "wellness":
      return [
        { field: "player_id", label: "Player ID", required: true },
        { field: "wellness_date", label: "Date", required: true },
        { field: "sleep_quality", label: "Sleep Quality (1-5)", required: false },
        { field: "fatigue", label: "Fatigue (1-5)", required: false },
        { field: "soreness", label: "Soreness (1-5)", required: false },
        { field: "stress", label: "Stress (1-5)", required: false },
        { field: "mood", label: "Mood (1-5)", required: false },
        { field: "notes", label: "Notes", required: false },
      ];
    case "cognitive":
      return [
        { field: "player_id", label: "Player ID", required: true },
        { field: "test_date", label: "Test Date", required: true },
        { field: "reaction_time_ms", label: "Reaction Time (ms)", required: false },
        { field: "go_no_go_accuracy_pct", label: "Go/No-Go Accuracy (%)", required: false },
        { field: "visual_search_ms", label: "Visual Search Time (ms)", required: false },
      ];
    case "fitness":
      return [
        { field: "player_id", label: "Player ID", required: true },
        { field: "test_date", label: "Test Date", required: true },
        { field: "test_category", label: "Test Category", required: true },
        { field: "test_name", label: "Test Name", required: true },
        { field: "value", label: "Value", required: false },
        { field: "unit", label: "Unit", required: false },
        { field: "left_value", label: "Left Value", required: false },
        { field: "right_value", label: "Right Value", required: false },
        { field: "asymmetry_percent", label: "Asymmetry (%)", required: false },
        { field: "threshold_flag", label: "Threshold Flag (green/yellow/red)", required: false },
        { field: "notes", label: "Notes", required: false },
      ];
    default:
      return [];
  }
}
